import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import axios from "../../../config/axiosConfig";
import { fieldSx } from "../../egresos/components/formStyles";

const isoFecha = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1950) return "";
  return String(value).slice(0, 10);
};
const RAZONES = ["Vacación", "Permiso", "Licencia", "Permiso por salud", "Permiso por estudio"];
// Días corridos entre dos fechas, ambas incluidas (sirve como referencia; los días reales se escriben a mano porque pueden ser medios días)
const diasEntre = (ini, fin) => {
  if (!ini || !fin) return null;
  const a = new Date(`${ini}T00:00:00`), b = new Date(`${fin}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return null;
  return Math.round((b - a) / 86400000) + 1;
};

// Editar una vacación o permiso ya emitido. Solo lo ven el rol admin y el rol conta con subrol cont (el servidor lo vuelve a comprobar).
const EditarLicenciaDialog = ({ open, onClose, registro, onGuardado }) => {
  const [form, setForm] = useState({ fecha_peticion: "", razon: "", fecha_ini: "", fecha_fin: "", cantidad: "", observacion: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && registro) {
      setForm({
        fecha_peticion: isoFecha(registro.fecha_peticion),
        razon: registro.razon || "",
        fecha_ini: isoFecha(registro.fecha_ini),
        fecha_fin: isoFecha(registro.fecha_fin),
        cantidad: String(registro.cantidad ?? ""),
        observacion: registro.observacion || "",
      });
      setError("");
    }
  }, [open, registro]);

  const cambiar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const dias = diasEntre(form.fecha_ini, form.fecha_fin);
  const rangoInvalido = form.fecha_ini && form.fecha_fin && form.fecha_fin < form.fecha_ini;
  const diasAntes = Number(registro?.cantidad);
  const diasAhora = Number(String(form.cantidad).replace(",", "."));
  const diferencia = Number.isFinite(diasAhora) && Number.isFinite(diasAntes) ? diasAhora - diasAntes : 0;
  const puedeGuardar = form.fecha_peticion && form.razon.trim() && form.fecha_ini && form.fecha_fin && !rangoInvalido && Number.isFinite(diasAhora) && diasAhora > 0 && !saving;

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.patch(`/api/permisos/licencia/${registro.id}`, { ...form, cantidad: diasAhora, razon: form.razon.trim(), observacion: form.observacion.trim() });
      onGuardado();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={guardar}>
      <DialogTitle sx={{ py: 1.25, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Editar vacación o permiso</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Registro #{registro?.id}</Typography>
      </DialogTitle>
      {/* pt con !important: MUI quita el relleno superior del contenido que sigue al título y la etiqueta del primer campo quedaba cortada */}
      <DialogContent sx={{ bgcolor: "background.default", p: 1.5, pt: "16px !important" }}>
        <Stack spacing={1.25}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
            <TextField size="small" type="date" label="Fecha de petición" required value={form.fecha_peticion} onChange={cambiar("fecha_peticion")} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} />
            <Autocomplete
              freeSolo size="small" options={RAZONES} value={form.razon} inputValue={form.razon}
              onInputChange={(_, v) => setForm((f) => ({ ...f, razon: v }))}
              renderInput={(p) => <TextField {...p} label="Razón" required sx={fieldSx} slotProps={{ htmlInput: { ...p.inputProps, maxLength: 60 } }} />}
            />
            <TextField size="small" type="date" label="Fecha de inicio" required value={form.fecha_ini} onChange={cambiar("fecha_ini")} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} />
            <TextField
              size="small" type="date" label="Fecha de finalización" required value={form.fecha_fin} onChange={cambiar("fecha_fin")} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx}
              error={!!rangoInvalido} helperText={rangoInvalido ? "No puede ser anterior al inicio" : ""}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" }, gap: 1, alignItems: "start" }}>
            <TextField size="small" type="number" label="Días" required value={form.cantidad} onChange={cambiar("cantidad")} sx={fieldSx} slotProps={{ htmlInput: { step: 0.5, min: 0 } }} />
            <Typography sx={{ fontSize: 12, color: "text.secondary", pt: 0.5 }}>
              {dias ? <>Entre las fechas hay <b>{dias}</b> día{dias === 1 ? "" : "s"} corrido{dias === 1 ? "" : "s"}. Los días que pongas aquí son los que se descuentan de las vacaciones (acepta medios días, ej. 0.5).</> : "Los días que pongas aquí son los que se descuentan de las vacaciones."}
            </Typography>
          </Box>
          <TextField size="small" label="Observación" multiline minRows={2} value={form.observacion} onChange={cambiar("observacion")} sx={fieldSx} />
          {diferencia !== 0 && (
            <Alert severity={diferencia > 0 ? "warning" : "info"} sx={{ py: 0 }}>
              {diferencia > 0 ? "Se descontarán " : "Se devolverán "}<b>{Math.abs(diferencia)}</b> día{Math.abs(diferencia) === 1 ? "" : "s"} {diferencia > 0 ? "más" : ""} del saldo de vacaciones del empleado.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={!puedeGuardar} sx={{ textTransform: "none", fontWeight: 700, minWidth: 150 }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditarLicenciaDialog;
